import { useEffect, useRef, useState } from "react";
import {
  CheckSquare,
  CalendarClock,
  Briefcase,
  LifeBuoy,
  ShoppingCart,
  FolderKanban,
  ChevronDown,
} from "lucide-react";

/**
 * ConvertFromButton — Phase 8B canonical conversion control.
 *
 * Every "create-from-this" UI across the app routes through this one
 * component so labels, icons, colours, and behaviour are identical
 * regardless of source entity (lead, ticket, message, schedule item, …).
 *
 * Per roadmap §Phase 8 — the canonical labels are:
 *   • Create Task
 *   • Create Event
 *   • Create Job card
 *   • Create Ticket
 *   • Create Procurement
 *   • Link to Project
 *
 * Trust Contract:
 *   • Rule 2 (no modals): the multi-target menu is an inline popover
 *     anchored to the trigger, NOT a full-screen overlay.  No `<Modal>`.
 *   • Rule 5 (mobile-first): 44px tap targets everywhere.
 *
 * Usage
 * -----
 *   // Single target — renders as a primary button
 *   <ConvertFromButton
 *     targets={["jobcard"]}
 *     onConvert={(t) => navigate("/jobcards/new?from_lead=" + lead.id)}
 *     testIdPrefix="lead-detail"
 *   />
 *
 *   // Multi-target — renders a trigger that opens an inline menu
 *   <ConvertFromButton
 *     targets={["task", "event", "jobcard", "ticket", "procurement"]}
 *     onConvert={(t) => handleConvert(t)}
 *     triggerLabel="Create from message"
 *     testIdPrefix="chat-msg"
 *   />
 */

/* ── Canonical target definitions ─────────────────────────────────────── */

export const TARGET_DEFS = {
  task:        { label: "Create Task",        Icon: CheckSquare,    color: "#10B981" },
  event:       { label: "Create Event",       Icon: CalendarClock,  color: "#A78BFA" },
  jobcard:     { label: "Create Job card",    Icon: Briefcase,      color: "#F59E0B" },
  ticket:      { label: "Create Ticket",      Icon: LifeBuoy,       color: "#F43F5E" },
  procurement: { label: "Create Procurement", Icon: ShoppingCart,   color: "#1FB6C1" },
  project:     { label: "Link to Project",    Icon: FolderKanban,   color: "#3B82F6" },
};

/* ── Component ─────────────────────────────────────────────────────────── */

export default function ConvertFromButton({
  targets,
  onConvert,
  triggerLabel,
  variant = "primary",        // "primary" | "ghost" | "small-ghost"
  disabled = false,
  testIdPrefix = "convert-from",
  className = "",
}) {
  const list = (targets || []).filter((t) => TARGET_DEFS[t]);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (list.length === 0) return null;

  const baseBtnClass = {
    primary:      "hv-btn hv-btn-primary text-xs",
    ghost:        "hv-btn hv-btn-ghost text-xs",
    "small-ghost": "hv-btn hv-btn-ghost !px-2 !py-0.5 text-[10px]",
  }[variant] || "hv-btn hv-btn-primary text-xs";

  // ── Single-target: render as a direct button ─────────────────────────
  if (list.length === 1) {
    const t = list[0];
    const def = TARGET_DEFS[t];
    return (
      <button
        type="button"
        onClick={() => !disabled && onConvert?.(t)}
        disabled={disabled}
        className={`${baseBtnClass} ${className}`}
        data-testid={`${testIdPrefix}-${t}-btn`}
        title={triggerLabel || def.label}
      >
        <def.Icon className="w-3.5 h-3.5" style={{ color: def.color }} />
        <span>{triggerLabel || def.label}</span>
      </button>
    );
  }

  // ── Multi-target: trigger + inline popover ───────────────────────────
  return (
    <div className="relative inline-block">
      <button
        type="button"
        ref={triggerRef}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={`${baseBtnClass} ${className}`}
        data-testid={`${testIdPrefix}-trigger`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span>{triggerLabel || "Create from this"}</span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          className="absolute right-0 z-50 mt-1 min-w-[200px] hv-card p-1 shadow-2xl animate-in fade-in zoom-in-95 duration-100"
          data-testid={`${testIdPrefix}-menu`}
        >
          {list.map((t) => {
            const def = TARGET_DEFS[t];
            return (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onConvert?.(t);
                }}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs text-left rounded hover:bg-white/[0.05] transition min-h-[44px]"
                data-testid={`${testIdPrefix}-${t}`}
                role="menuitem"
              >
                <def.Icon className="w-3.5 h-3.5 shrink-0" style={{ color: def.color }} />
                <span className="text-[#cbd2df]">{def.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
